import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import api from '../api/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  LinearProgress,
  Chip,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Loyalty as LoyaltyIcon,
  Star as StarIcon,
  LocalOffer as OfferIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { AppIconButton } from '../components/ui';
import { format } from 'date-fns';
import { CURRENCY } from '../config/currency';

interface LoyaltyProgress {
  id: number;
  programId: number;
  programName: string;
  programType: string;
  programSummary: string;
  vendorId: number;
  vendorName: string;
  qualifyingVisits: number;
  qualifyingSpend: number;
  pointsBalance: number;
  itemPurchaseCount: number;
  rewardAvailable: boolean;
  rewardValue: number | null;
  rewardEarnedAt: string | null;
  rewardExpiresAt: string | null;
  progressPercentage: number;
  progressDisplay: string;
  remainingToReward: number;
  cyclesCompleted: number;
  badgeColor: string;
  badgeIcon: string;
  rewardType: string;
  rewardDescription?: string;
}

const CustomerRewards: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState<LoyaltyProgress[]>([]);
  const [expandedVendors, setExpandedVendors] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (token) {
      fetchRewards();
    }
  }, [token]);

  const fetchRewards = async () => {
    try {
      const response = await api.get('/api/loyalty/customer/rewards');
      setRewards(response.data);
      // Expand all vendors by default
      const vendorIds = new Set(response.data.map((r: LoyaltyProgress) => r.vendorId));
      setExpandedVendors(vendorIds);
    } catch (error) {
      console.error('Error fetching rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVendor = (vendorId: number) => {
    setExpandedVendors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vendorId)) {
        newSet.delete(vendorId);
      } else {
        newSet.add(vendorId);
      }
      return newSet;
    });
  };

  // Group rewards by vendor
  const rewardsByVendor = rewards.reduce((acc, reward) => {
    if (!acc[reward.vendorId]) {
      acc[reward.vendorId] = {
        vendorName: reward.vendorName,
        rewards: [],
      };
    }
    acc[reward.vendorId].rewards.push(reward);
    return acc;
  }, {} as Record<number, { vendorName: string; rewards: LoyaltyProgress[] }>);

  const getProgramTypeIcon = (type: string) => {
    switch (type) {
      case 'VISIT_BASED': return '🎯';
      case 'SPEND_BASED': return '💰';
      case 'POINTS_BASED': return '⭐';
      case 'ITEM_BASED': return '🎁';
      default: return '🎁';
    }
  };

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return '₹0';
    return `${CURRENCY.symbol}${amount.toLocaleString(CURRENCY.locale)}`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'MMM d, yyyy');
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.background, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AppIconButton
            icon={<ArrowBackIcon />}
            tooltip="Go back"
            variant="ghost"
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.text }}>
            My Rewards
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.background, pb: 10 }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryHover || theme.primary})`,
        p: 2,
        pb: 4,
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AppIconButton
            icon={<ArrowBackIcon />}
            tooltip="Go back"
            variant="ghost"
            onClick={() => navigate(-1)}
            sx={{ color: 'white', mr: 1 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            My Rewards
          </Typography>
        </Box>

        {/* Summary Card */}
        <Card sx={{ backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: theme.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <LoyaltyIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.text }}>
                  {rewards.filter(r => r.rewardAvailable).length}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.text, opacity: 0.7 }}>
                  Rewards Ready to Use
                </Typography>
              </Box>
            </Box>

            {rewards.filter(r => r.rewardAvailable).length > 0 && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#2EB67D15', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: 'var(--status-ok)', fontWeight: 500 }}>
                  You have rewards waiting! Visit these restaurants to redeem.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, mt: -2 }}>
        {Object.keys(rewardsByVendor).length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6, mt: 4 }}>
            <CardContent>
              <LoyaltyIcon sx={{ fontSize: 64, color: theme.primary, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" sx={{ color: theme.text, mb: 1 }}>
                No loyalty programs yet
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text, opacity: 0.7, mb: 3 }}>
                Start ordering from restaurants with loyalty programs to earn rewards!
              </Typography>
              <Button
                variant="contained"
                startIcon={<RestaurantIcon />}
                onClick={() => navigate('/menu')}
                sx={{ backgroundColor: theme.primary }}
              >
                Browse Restaurants
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(rewardsByVendor).map(([vendorId, vendor]) => {
              const hasRewardReady = vendor.rewards.some(r => r.rewardAvailable);
              const isExpanded = expandedVendors.has(parseInt(vendorId));

              return (
                <Card key={vendorId} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  {/* Vendor Header */}
                  <Box
                    onClick={() => toggleVendor(parseInt(vendorId))}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      backgroundColor: hasRewardReady ? '#2EB67D15' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: theme.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <RestaurantIcon sx={{ fontSize: 24, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
                          {vendor.vendorName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.text, opacity: 0.7 }}>
                          {vendor.rewards.length} program{vendor.rewards.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {hasRewardReady && (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          label="Reward Ready!"
                          size="small"
                          sx={{
                            backgroundColor: 'var(--action)',
                            color: 'white',
                            '& .MuiChip-icon': { color: 'white' },
                          }}
                        />
                      )}
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                  </Box>

                  {/* Rewards List */}
                  <Collapse in={isExpanded}>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {vendor.rewards.map((reward) => (
                        <Box
                          key={reward.id}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `2px solid ${reward.rewardAvailable ? '#2EB67D' : '#e0e0e0'}`,
                            backgroundColor: reward.rewardAvailable ? '#2EB67D08' : 'transparent',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              backgroundColor: reward.badgeColor || theme.primary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 20,
                            }}>
                              {getProgramTypeIcon(reward.programType)}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.text }}>
                                {reward.programName}
                              </Typography>
                              <Typography variant="body2" sx={{ color: theme.text, opacity: 0.7, mt: 0.5 }}>
                                {reward.programSummary}
                              </Typography>

                              {/* Progress or Reward */}
                              {reward.rewardAvailable ? (
                                <Box sx={{ mt: 2 }}>
                                  <Box sx={{
                                    p: 1.5,
                                    backgroundColor: 'var(--action)',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}>
                                    <Box>
                                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                        {reward.rewardDescription || formatCurrency(reward.rewardValue) + ' off'}
                                      </Typography>
                                      {reward.rewardExpiresAt && (
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <ScheduleIcon sx={{ fontSize: 12 }} />
                                          Expires {formatDate(reward.rewardExpiresAt)}
                                        </Typography>
                                      )}
                                    </Box>
                                    <StarIcon sx={{ color: 'white' }} />
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ mt: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: theme.text, opacity: 0.7 }}>
                                      {reward.progressDisplay}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.primary, fontWeight: 600 }}>
                                      {reward.progressPercentage}%
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={reward.progressPercentage}
                                    sx={{
                                      height: 8,
                                      borderRadius: 4,
                                      backgroundColor: 'var(--bg-sunken)',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        backgroundColor: reward.badgeColor || theme.primary,
                                      },
                                    }}
                                  />
                                  {reward.remainingToReward > 0 && (
                                    <Typography variant="caption" sx={{ color: theme.text, opacity: 0.7, mt: 0.5, display: 'block' }}>
                                      {reward.programType === 'VISIT_BASED' && `${reward.remainingToReward} more order${reward.remainingToReward > 1 ? 's' : ''} to go!`}
                                      {reward.programType === 'SPEND_BASED' && `Spend ${formatCurrency(reward.remainingToReward)} more to unlock!`}
                                      {reward.programType === 'ITEM_BASED' && `${reward.remainingToReward} more item${reward.remainingToReward > 1 ? 's' : ''} to go!`}
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* Cycles completed badge */}
                              {reward.cyclesCompleted > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Chip
                                    size="small"
                                    label={`${reward.cyclesCompleted}x redeemed`}
                                    sx={{ backgroundColor: 'var(--bg-sunken)', fontSize: '0.7rem' }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomerRewards;
